
import Vue from "vue";
import { CandidateAiAssessmentsViewOptions } from "@/interfaces/candidate/canidate_assessments";
import { minLength, required } from "vuelidate/lib/validators";

export default Vue.extend({
  name: "QuestionComponent",
  props: {
    question: {
      type: Object,
      required: true
    },
    questionIndex: {
      type: Number,
      required: true
    },
    isLastQuestion: {
      type: Boolean,
      default: false
    },
    submitting: {
      type: Boolean,
      default: false
    },
    currentAnswer: {
      type: String,
      default: ""
    }
  },
  beforeDestroy() {
    if (this.timerId) clearInterval(this.timerId);
  },
  data() {
    return {
      selectedAnswer: "",
      CandidateAiAssessmentsViewOptions,
      remainingSeconds: 0,
      timerId: null as number | null
    };
  },
  computed: {
    requiredError() {
      const errors: string[] = [];
      if (!this.$v.selectedAnswer?.$dirty) return errors;
      if (!this.$v.selectedAnswer?.required) {
        errors.push(this.$t("rules.required") as string);
      }
      // Only enforce minimum length for SHORT_ANSWER questions.
      if (
        this.question?.question_type ===
        CandidateAiAssessmentsViewOptions.SHORT_ANSWER
      ) {
        if (!this.$v.selectedAnswer?.minLength) {
          errors.push(this.$t("rules.minLength", { min: 4 }) as string);
        }
      }

      return errors;
    }
  },
  watch: {
    question: {
      handler() {
        this.startTimer();
        // Set the answer from currentAnswer prop if available, otherwise clear it
        this.selectedAnswer = this.currentAnswer || "";
      },
      immediate: true
    },
    currentAnswer: {
      handler(newAnswer: string) {
        // Update selectedAnswer when currentAnswer prop changes
        this.selectedAnswer = newAnswer || "";
      },
      immediate: true
    },
    submitting: {
      handler(newVal: boolean) {
        if (!newVal) {
          // Reset validation state when not submitting
          this.$v.$reset();
        }
      },
      immediate: true
    }
  },
  // Form validations
  validations() {
    const isShortAnswer =
      this.question?.question_type ===
      CandidateAiAssessmentsViewOptions.SHORT_ANSWER;

    return {
      selectedAnswer: {
        required,
        // MCQ / True-False: only required; Short Answer: require at least 4 characters.
        ...(isShortAnswer ? { minLength: minLength(4) } : {})
      }
    };
  },
  methods: {
    event_cancellation(event: Event) {
      event.preventDefault();
    },
    getQuestionTypeColor(questionType: string) {
      const colors = {
        [CandidateAiAssessmentsViewOptions.MULTIPLE_CHOICE]: "primary",
        [CandidateAiAssessmentsViewOptions.SHORT_ANSWER]: "primary",
        [CandidateAiAssessmentsViewOptions.TRUE_FALSE]: "primary"
      };
      return colors[questionType as keyof typeof colors] || "primary";
    },
    isArabic() {
      return localStorage.getItem("locale") === "ar";
    },
    getQuestionTypeIcon(questionType: string) {
      const icons = {
        [CandidateAiAssessmentsViewOptions.MULTIPLE_CHOICE]:
          "mdi-checkbox-multiple-marked",
        [CandidateAiAssessmentsViewOptions.SHORT_ANSWER]: "mdi-text",
        [CandidateAiAssessmentsViewOptions.TRUE_FALSE]: "mdi-check-circle"
      };
      return icons[questionType as keyof typeof icons] || "mdi-help-circle";
    },
    formatQuestionType(questionType: string) {
      const labels = {
        [CandidateAiAssessmentsViewOptions.MULTIPLE_CHOICE]: this.$t(
          "ai-assessments.header.multiple-choice"
        ) as string,
        [CandidateAiAssessmentsViewOptions.SHORT_ANSWER]: this.$t(
          "ai-assessments.header.short-answer"
        ) as string,
        [CandidateAiAssessmentsViewOptions.TRUE_FALSE]: this.$t(
          "ai-assessments.header.true-false"
        ) as string
      };
      const label = labels[questionType as keyof typeof labels];
      return label
        ? label.toString()
        : this.$t("ai-assessments.question.unknown").toString();
    },
    startTimer() {
      if (this.timerId) {
        clearInterval(this.timerId);
      }

      if (this.question.question_time_allowed) {
        this.remainingSeconds = this.question.question_time_allowed;

        this.timerId = setInterval(() => {
          if (this.remainingSeconds > 0) {
            this.remainingSeconds--;
          } else {
            if (this.timerId) {
              window.clearInterval(this.timerId);
            }
            this.submitAnswer(true);
          }
        }, 1000);
      }
    },
    submitAnswer(ignoreValidation?: boolean) {
      if (typeof ignoreValidation !== "boolean" || !ignoreValidation) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      }

      this.$emit("answer-submitted", {
        questionId: this.question.id,
        answer: this.selectedAnswer,
        questionType: this.question.question_type
      });
    }
  }
});
